import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { 
  FaPhoneAlt, 
  FaEnvelope, 
  FaWhatsapp, 
  FaArrowRight, 
  FaShieldAlt, 
  FaClock, 
  FaCheckCircle,
  FaHeadset,
  FaComments,
  FaVideo,
  FaUserCheck
} from "react-icons/fa";
import "../styles/CtaSection.css";

const CtaSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const benefits = [
    { icon: <FaShieldAlt />, text: "100% Confidential" },
    { icon: <FaClock />, text: "24/7 Emergency Support" },
    { icon: <FaCheckCircle />, text: "Free Consultation" },
    { icon: <FaUserCheck />, text: "Expert Detectives" },
  ];

  return (
    <section className="cta-premium" ref={sectionRef}>
      {/* Animated Background */}
      <div className="cta-premium-bg">
        <div className="bg-gradient-1"></div>
        <div className="bg-gradient-2"></div>
        <div className="bg-gradient-3"></div>
        <div className="bg-particles">
          {[...Array(20)].map((_, i) => (
            <div key={i} className="particle" style={{ animationDelay: `${i * 0.5}s` }}></div>
          ))}
        </div>
      </div>

      <div className="cta-premium-container">
        <div className="cta-premium-grid">
          {/* Left Side - Content */}
          <div className={`cta-premium-content ${isVisible ? "visible" : ""}`}>
            <div className="cta-badge">
              <FaHeadset className="badge-icon" />
              <span>24/7 Emergency Support</span>
            </div>
            
            <h2 className="cta-premium-title">
              Need Confidential
              <span className="title-gradient"> Investigation Services?</span>
            </h2>
            
            <div className="title-underline">
              <span></span>
              <span></span>
              <span></span>
            </div>
            
            <p className="cta-premium-description">
              Get immediate assistance from our expert team. We're available round the clock 
              to handle your investigation needs with utmost confidentiality and professionalism.
            </p>
            
            <div className="cta-benefits">
              {benefits.map((benefit, index) => (
                <div key={index} className="benefit-item" style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="benefit-icon">{benefit.icon}</div>
                  <span>{benefit.text}</span>
                </div>
              ))}
            </div>
            
            <div className="cta-premium-buttons">
              <a href="tel:+917428203483" className="cta-premium-phone">
                <FaPhoneAlt className="btn-icon" />
                <div className="btn-text">
                  <span className="btn-label">Call Now For Emergency</span>
                  <span className="btn-number">+91 7428203483</span>
                </div>
                <FaArrowRight className="btn-arrow" />
              </a>
              
              <div className="cta-secondary-buttons">
                <Link to="/contact-us" className="cta-premium-contact">
                  <FaEnvelope />
                  <span>Email Us</span>
                </Link>
                <a href="https://wa.me/9187428203483" className="cta-premium-whatsapp" target="_blank" rel="noopener noreferrer">
                  <FaWhatsapp />
                  <span>WhatsApp</span>
                </a>
              </div>
            </div>
          </div>

          {/* Right Side - Contact Card */}
          <div className={`cta-premium-card ${isVisible ? "visible" : ""}`}>
            <div className="card-glow"></div>
            <div className="card-header">
              <div className="card-icon">
                <FaComments />
              </div>
              <h3>Free Consultation</h3>
              <p>Get a free, no-obligation consultation with our expert team</p>
            </div>
            
            <div className="card-features">
              <div className="card-feature">
                <FaCheckCircle className="feature-check" />
                <div>
                  <strong>Quick Response</strong>
                  <span>Within 30 minutes</span>
                </div>
              </div>
              <div className="card-feature">
                <FaCheckCircle className="feature-check" />
                <div>
                  <strong>Expert Advice</strong>
                  <span>From licensed professionals</span>
                </div>
              </div>
              <div className="card-feature">
                <FaCheckCircle className="feature-check" />
                <div>
                  <strong>No Obligation</strong>
                  <span>Free initial consultation</span>
                </div>
              </div>
            </div>
            
            <div className="card-testimonial">
              <div className="testimonial-quote">"</div>
              <p>They responded within minutes and provided excellent guidance. Highly professional!</p>
              <div className="testimonial-author">
                <strong>- Vikram Singh</strong>
                <span>Verified Client</span>
              </div>
            </div>
            
            <div className="card-availability">
              <div className="availability-dot"></div>
              <span>Experts available now</span>
            </div>
          </div>
        </div>

        {/* Trust Strip */}
        <div className="trust-strip">
          <div className="trust-item">
            <FaShieldAlt />
            <span>Govt Licensed</span>
          </div>
          <div className="trust-divider"></div>
          <div className="trust-item">
            <FaCheckCircle />
            <span>98% Success Rate</span>
          </div>
          <div className="trust-divider"></div>
          <div className="trust-item">
            <FaClock />
            <span>24/7 Support</span>
          </div>
          <div className="trust-divider"></div>
          <div className="trust-item">
            <FaUserCheck />
            <span>10k+ Clients</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CtaSection;